import { isOnPrem } from '@/utils/buildProfile';
import {
  OpenFeature,
  useBooleanFlagValue,
  useObjectFlagValue,
  useStringFlagValue,
} from '@openfeature/react-sdk';
import realPosthog from 'posthog-js';
import {
  useFeatureFlagEnabled as usePosthogFeatureFlagEnabled,
  useFeatureFlagPayload as usePosthogFeatureFlagPayload,
  useFeatureFlagVariantKey as usePosthogFeatureFlagVariantKey,
} from 'posthog-js/react';
// Type-only import — exempt from the F1 on-prem SDK gate (no runtime load).
import type { JsonType } from 'posthog-js';

import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';

/**
 * On-prem fallback for PostHog feature-flag reads.
 *
 * PostHog can't run on-prem (airgap; F1 guardrail), so flags read through
 * posthog-js return code defaults there. These shims keep cloud behaviour
 * byte-identical (delegate to posthog) but, on-prem, read the same flag from
 * the in-process bundle via OpenFeature — the provider `OnPremFlagBundleSync`
 * seeds. This is a transparent swap, NOT a migration: cloud still resolves
 * PostHog-only flags from PostHog, so call sites keep working on both builds.
 *
 * posthog-js imports here are referenced only in the cloud branch and are
 * dead-code-eliminated on the on-prem build (same pattern as
 * `posthog-provider.tsx`), so nothing phones home.
 */

interface FlagReadApi {
  isFeatureEnabled(key: string, options?: { send_event?: boolean }): boolean | undefined;
  getFeatureFlag(key: string): boolean | string | undefined;
  getFeatureFlagPayload(key: string): JsonType;
}

const ofClient = () => OpenFeature.getClient(FEATURE_FLAG_DOMAIN);

const onPremFlagApi: FlagReadApi = {
  isFeatureEnabled: (key) => ofClient().getBooleanValue(key, false),
  // posthog returns a boolean for simple flags, else a variant string.
  // Probe boolean first (the common kind) so a boolean read costs no
  // OpenFeature error hooks — the typed getters throw a TypeMismatchError on
  // every kind mismatch. `*Details` surfaces that mismatch as `errorCode`
  // instead of throwing, so a string/multivariate flag falls through cleanly
  // and a legitimately-empty variant ('') is preserved rather than mistaken
  // for "not a string flag".
  getFeatureFlag: (key) => {
    const client = ofClient();
    const boolean = client.getBooleanDetails(key, false);
    if (boolean.errorCode === undefined) {
      return boolean.value;
    }
    const string = client.getStringDetails(key, '');
    return string.errorCode === undefined ? string.value : undefined;
  },
  getFeatureFlagPayload: (key) => ofClient().getObjectValue(key, null) as JsonType,
};

/**
 * Drop-in for the posthog-js default export, narrowed to the flag-read methods.
 * Swap `import posthog from 'posthog-js'` → `import { posthog } from
 * '@/app-shell/flags/posthog-compat'` in modules that use posthog ONLY to read
 * flags (e.g. `src/components/constants/flags.ts`).
 *
 * `isOnPrem` is read inside each method (call time), never at module load —
 * reading it eagerly here would trip test suites that mock `buildProfile` with
 * a lazily-initialised getter. The on-prem build still folds `isOnPrem` to a
 * literal and DCE's the cloud branch (posthog-js), so nothing phones home (F1).
 */
export const posthog: FlagReadApi = {
  isFeatureEnabled: (key, options) =>
    isOnPrem
      ? onPremFlagApi.isFeatureEnabled(key)
      : options
        ? realPosthog.isFeatureEnabled(key, options)
        : realPosthog.isFeatureEnabled(key),
  getFeatureFlag: (key) =>
    isOnPrem ? onPremFlagApi.getFeatureFlag(key) : realPosthog.getFeatureFlag(key),
  getFeatureFlagPayload: (key) =>
    isOnPrem
      ? onPremFlagApi.getFeatureFlagPayload(key)
      : realPosthog.getFeatureFlagPayload(key),
};

// Note: For development - set via NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS env var
const FORCE_ENABLE_ALL_FEATURE_FLAGS =
  process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true';
if (FORCE_ENABLE_ALL_FEATURE_FLAGS) {
  const ph = posthog as typeof posthog & {
    isFeatureEnabled: () => boolean;
    getFeatureFlag: () => string;
  };
  ph.isFeatureEnabled = () => true;
  ph.getFeatureFlag = () => 'variant-1';
}

/** Drop-in for `posthog-js/react`'s `useFeatureFlagEnabled`. */
export function useFeatureFlagEnabled(key: string): boolean | undefined {
  // `isOnPrem` is build-folded, so exactly one branch survives DCE — the
  // conditional hook call is safe despite the lint rule.
  if (isOnPrem) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBooleanFlagValue(key, false);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return usePosthogFeatureFlagEnabled(key);
}

/** Drop-in for `posthog-js/react`'s `useFeatureFlagVariantKey`. */
export function useFeatureFlagVariantKey(key: string): string | boolean | undefined {
  // `isOnPrem` is build-folded, so exactly one branch survives DCE — the
  // conditional hook call is safe despite the lint rule.
  if (isOnPrem) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStringFlagValue(key, '');
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return usePosthogFeatureFlagVariantKey(key);
}

/** Drop-in for `posthog-js/react`'s `useFeatureFlagPayload`. */
export function useFeatureFlagPayload(key: string): JsonType {
  // `isOnPrem` is build-folded, so exactly one branch survives DCE — the
  // conditional hook call is safe despite the lint rule.
  if (isOnPrem) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useObjectFlagValue(key, null) as JsonType;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return usePosthogFeatureFlagPayload(key) as JsonType;
}
